/**
 * The three things someone can choose. "system" is the default, and it is not
 * a third colour scheme — it means "whatever the operating system is asking
 * for right now", which can change while the app is open.
 */
export type ThemeMode = "system" | "light" | "dark";

const THEME_MODES: ThemeMode[] = ["system", "light", "dark"];

/**
 * Exported so the listener that watches the OS cannot drift from the read that
 * seeds the store. Both have to ask the same question.
 */
export const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * What the operating system is asking for at this instant.
 *
 * The app runs with `ssr: false`, so `window` is really there by the time a
 * store is created. The guard is for the odd non-browser context all the same —
 * a unit test, or a build step that imports the store — where the honest answer
 * is "no opinion", and light is this app's long-standing no-opinion answer.
 */
export function systemPrefersDark(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  return window.matchMedia(SYSTEM_DARK_QUERY).matches;
}

export const useThemeStore = definePiniaStore("theme", {
  state: () => ({
    /*
     * The CHOICE, and the only part of the theme that is persisted. The flag
     * the app actually renders from is derived below.
     *
     * This used to be a single boolean, which cannot tell "this person picked
     * light" apart from "this person has never touched the setting". Those two
     * want opposite behaviour: the first must be obeyed, the second must follow
     * the OS. Three states say which one we are looking at.
     */
    themeMode: "system" as ThemeMode,

    /*
     * The OS preference, mirrored into the store so the getters below can be
     * plain synchronous reads. Seeded here rather than on mount because the
     * layout paints its `dark` class from the very first render — a value that
     * only arrived later would show as a flash of the wrong theme.
     *
     * Deliberately NOT persisted. It is a fact about the machine, not a
     * decision, and it is re-read on every load; storing it would only give us
     * a stale copy to disagree with.
     */
    osPrefersDark: systemPrefersDark(),

    pageTitle: "",
  }),

  persist: {
    /*
     * `paths` keeps the derived flag out of storage. `darkTheme` is a getter
     * now, so it is not in `$state` and could not be written anyway, but saying
     * so here is what stops someone re-adding it to state later and quietly
     * bringing the old bug back.
     *
     * `pageTitle` stays persisted exactly as it always was. Nothing about this
     * change concerns it.
     */
    paths: ["themeMode", "pageTitle"],

    serializer: {
      serialize: JSON.stringify,

      /*
       * MIGRATING THE OLD PERSISTED BOOLEAN.
       *
       * Every browser that has ever run this app already has `{"darkTheme":
       * false, ...}` under the "theme" key. The persistence plugin restores by
       * patching the whole stored object into the store and ignores `paths`
       * while doing it, so that dead key would land on top of a getter of the
       * same name. Dropping it here, at the one point every restore passes
       * through, is the only place that reliably catches it.
       *
       * What that means for people: a browser holding only the old boolean has
       * no recorded CHOICE, so it lands on "system" and follows the OS from
       * then on. Someone who had deliberately picked dark is therefore moved to
       * system once, and will have to pick dark again if their OS is light.
       * That is the intended reading of "make sure the theme value is taken
       * from system by default" — the alternative is that the new default never
       * reaches a single existing install, because every one of them is
       * carrying a stored `false` that is indistinguishable from a choice.
       */
      deserialize: (stored: string) => {
        const restored = JSON.parse(stored);
        if (restored && typeof restored === "object") {
          delete (restored as Record<string, unknown>).darkTheme;
        }
        return restored;
      },
    },
  },

  actions: {
    /** Record an explicit choice. Unknown values are ignored rather than stored. */
    setThemeMode(mode: ThemeMode) {
      if (!THEME_MODES.includes(mode)) return;
      this.themeMode = mode;
    },

    /*
     * Kept for any caller that just wants the opposite of what is on screen.
     * Flipping is by definition an explicit choice, so this leaves "system"
     * behind rather than trying to invert it — there is no opposite of
     * "follow the OS".
     */
    toggleTheme() {
      this.themeMode = this.darkTheme ? "light" : "dark";
    },

    /*
     * Called by `useSystemThemeSync` when the OS flips while the app is open.
     * Nothing else should write this.
     */
    syncSystemPreference(prefersDark: boolean) {
      this.osPrefersDark = prefersDark;
    },

    setPageTitle(title: string) {
      this.$state.pageTitle = title;
    },
  },

  getters: {
    /*
     * The effective flag: true when the app should render dark, whatever the
     * reason. This is still spelled `darkTheme` and still returns a boolean, so
     * `useChartTheme` and `useAccessibleAccent` read it exactly as before — the
     * only difference is that it is now computed instead of stored, which is
     * what lets "system" track the OS live without anyone writing to it.
     */
    darkTheme: (state): boolean =>
      state.themeMode === "system"
        ? state.osPrefersDark
        : state.themeMode === "dark",

    /** The layout's `dark` class binding reads this. Unchanged in meaning. */
    getTheme(): boolean {
      return this.darkTheme;
    },

    title: (state) => state.pageTitle,
  },
});
